import { KeyRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import { CopyButton } from '~/components/CopyButton'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { tourAnchor } from '~/helpers/tour/anchors'

type IProps = {
  /** Le secret en clair, juste après sa génération ; il ne sera plus jamais montré. */
  revealed: string | null
  /** Une destination existe : il y a un secret à remplacer. */
  configured: boolean
  onRegenerate: () => void
}

/** Le secret qui signe chaque appel (#5), et comment le destinataire vérifie la signature. */
function WebhookSecretCard({ revealed, configured, onRegenerate }: IProps) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)

  return (
    <Card {...tourAnchor('webhooks.secret')}>
      <CardHeader>
        <CardTitle>{t('webhooks.secret.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {revealed
          ? (
              <div className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                <p>{t('webhooks.secret.once')}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded bg-background px-2 py-1 text-xs">{revealed}</code>
                  <CopyButton
                    value={revealed}
                    label={t('webhooks.secret.copy')}
                    copiedMessage={t('webhooks.secret.copied')}
                    failedMessage={t('webhooks.secret.copyFailed')}
                  />
                </div>
              </div>
            )
          : <p className="text-muted-foreground">{t(configured ? 'webhooks.secret.hidden' : 'webhooks.secret.none')}</p>}
        <p className="text-xs text-muted-foreground">{t('webhooks.secret.verify')}</p>
        {configured && (
          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
              <KeyRound className="mr-2 size-4" />
              {t('webhooks.secret.regenerate')}
            </Button>
          </div>
        )}
        {/* L'ancien secret cesse aussitôt de signer : un clic de travers casserait l'intégration. */}
        <ConfirmDialog
          open={confirming}
          title={t('webhooks.secret.regenerateTitle')}
          confirmLabel={t('webhooks.secret.regenerate')}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false)
            onRegenerate()
          }}
        >
          {t('webhooks.secret.regenerateWarning')}
        </ConfirmDialog>
      </CardContent>
    </Card>
  )
}

export default WebhookSecretCard
